import { format } from 'date-fns'

/**
 * Génère le PDF d'une facture.
 * @param {object} facture - La facture enrichie avec .patients et .consultations
 * @param {object} praticien - Le praticien (prenom, nom, adresse_cabinet, etc.)
 * @param {boolean} returnBase64 - Si true, retourne le base64 au lieu de télécharger
 * @returns {Promise<string|null>}
 */
export function generateFacturePDF(facture, praticien, returnBase64 = false) {
  return new Promise((resolve) => {
    import('jspdf').then(({ jsPDF }) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF({ unit: 'mm', format: 'a4' })
        const pat = facture.patients
        const W = 210
        const margin = 20

        // EN-TETE GAUCHE
        doc.setFontSize(15)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('CABINET OSTEOPATHIE', margin, 28)

        doc.setFontSize(11)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(50, 50, 50)
        doc.text(praticien.prenom + ' ' + praticien.nom, margin, 36)

        let yInfo = 42
        if (praticien.adresse_cabinet) {
          const lignes = doc.splitTextToSize(praticien.adresse_cabinet, 90)
          lignes.forEach(l => { doc.text(l, margin, yInfo); yInfo += 5 })
        }
        if (praticien.telephone) { doc.text('Tel : ' + praticien.telephone, margin, yInfo); yInfo += 5 }
        if (praticien.numero_rpps) { doc.text('N RPPS : ' + praticien.numero_rpps, margin, yInfo); yInfo += 5 }
        if (praticien.siret) { doc.text('SIRET : ' + praticien.siret, margin, yInfo) }

        // EN-TETE DROITE
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('FACTURE N ' + facture.numero, W - margin, 28, { align: 'right' })

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(80, 80, 80)
        doc.text('Date : ' + format(new Date(facture.date_facture), 'dd/MM/yyyy'), W - margin, 36, { align: 'right' })

        // LIGNE SEPARATRICE
        doc.setDrawColor(180, 180, 180)
        doc.setLineWidth(0.3)
        doc.line(margin, 62, W - margin, 62)

        // BLOC PATIENT
        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(120, 120, 120)
        doc.text('PATIENT :', margin, 70)

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text((pat?.prenom || '') + ' ' + (pat?.nom || ''), margin, 78)

        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(70, 70, 70)
        let yPat = 84
        if (pat?.date_naissance) {
          const age = Math.floor((new Date() - new Date(pat.date_naissance)) / (365.25 * 24 * 3600 * 1000))
          doc.text('Ne(e) le ' + format(new Date(pat.date_naissance), 'dd/MM/yyyy') + ' (' + age + ' ans)', margin, yPat)
          yPat += 5
        }
        if (pat?.adresse) {
          const lignes = doc.splitTextToSize(pat.adresse, 100)
          lignes.forEach(l => { doc.text(l, margin, yPat); yPat += 5 })
        }
        if (pat?.code_postal || pat?.ville) {
          doc.text([pat?.code_postal, pat?.ville].filter(Boolean).join(' '), margin, yPat)
          yPat += 5
        }

        // TABLEAU PRESTATIONS
        const dureeLabel = facture.consultations?.duree_minutes
          ? facture.consultations.duree_minutes + ' min' : '-'
        const startY = Math.max(yPat + 8, 105)

        doc.autoTable({
          startY,
          head: [['Prestation', 'Duree', 'Montant']],
          body: [['Consultation osteopathie', dureeLabel, facture.montant + ' euros']],
          styles: {
            fontSize: 11,
            cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
            textColor: [20, 20, 20],
            lineColor: [210, 210, 210],
            lineWidth: 0.2,
          },
          headStyles: {
            fillColor: [30, 30, 30],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10,
          },
          columnStyles: {
            0: { cellWidth: 115 },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 30, halign: 'right' },
          },
          margin: { left: margin, right: margin },
          tableWidth: W - margin * 2,
        })

        const yTable = doc.lastAutoTable.finalY

        // TOTAL
        doc.setDrawColor(30, 30, 30)
        doc.setLineWidth(0.5)
        doc.line(margin, yTable + 5, W - margin, yTable + 5)

        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(20, 20, 20)
        doc.text('TOTAL :', margin, yTable + 16)
        doc.text(facture.montant + ' euros', W - margin, yTable + 16, { align: 'right' })

        doc.setLineWidth(0.3)
        doc.setDrawColor(180, 180, 180)
        doc.line(margin, yTable + 20, W - margin, yTable + 20)

        // REGLEMENT
        let yBottom = yTable + 30
        if (facture.statut === 'payee') {
          doc.setFontSize(10)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(100, 100, 100)
          let reglLabel = 'Reglement recu'
          if (facture.date_paiement) {
            reglLabel += ' le ' + format(new Date(facture.date_paiement), 'dd/MM/yyyy')
          }
          doc.text(reglLabel, margin, yBottom)
          yBottom += 8
        }
        if (facture.notes) {
          doc.setFontSize(9)
          doc.setTextColor(120, 120, 120)
          doc.text(facture.notes, margin, yBottom)
          yBottom += 8
        }

        // ZONE SIGNATURE
        const ySig = Math.max(yBottom + 10, yTable + 50)
        if (ySig < 240) {
          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(120, 120, 120)
          doc.text('Signature :', margin, ySig)
          doc.setDrawColor(200, 200, 200)
          doc.setLineWidth(0.3)
          doc.rect(margin, ySig + 3, 60, 28)
          if (praticien.signature_base64) {
            try {
              doc.addImage(praticien.signature_base64, 'PNG', margin + 2, ySig + 5, 56, 24)
            } catch (e) { /* signature invalide */ }
          }
        }

        // TAMPON PAYEE
        if (facture.statut === 'payee') {
          const cx = W / 2
          const cy = 240
          doc.saveGraphicsState()
          doc.setGState(new doc.GState({ opacity: 0.18 }))
          doc.setFontSize(52)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(200, 30, 30)
          doc.text('PAYEE', cx, cy, { align: 'center', angle: 30 })
          doc.restoreGraphicsState()
          doc.saveGraphicsState()
          doc.setGState(new doc.GState({ opacity: 0.25 }))
          doc.setDrawColor(200, 30, 30)
          doc.setLineWidth(1.5)
          doc.setFontSize(48)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(200, 30, 30)
          doc.text('PAYEE', cx, cy, { align: 'center', angle: 30, renderingMode: 'stroke' })
          doc.restoreGraphicsState()
        }

        // PIED DE PAGE
        doc.setDrawColor(190, 190, 190)
        doc.setLineWidth(0.2)
        doc.line(margin, 272, W - margin, 272)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(140, 140, 140)
        doc.text('TVA non applicable - Article 261 4-1 du CGI', W / 2, 278, { align: 'center' })

        if (returnBase64) {
          const pdfBase64 = doc.output('datauristring').split(',')[1]
          resolve(pdfBase64)
        } else {
          doc.save(facture.numero + '.pdf')
          resolve(null)
        }
      })
    })
  })
}
